import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  Save,
  Loader2,
  Shield,
  Calculator,
} from 'lucide-react';
import {
  computeRenewal,
  getDefaultRenewalInputs,
  dbRowsToRenewalInputs,
  renewalInputsToDbRows,
  type RenewalInputs,
} from '@/lib/renewal-pricing';

import {
  formatGHS,
} from '@/lib/valuation';

// ─── Display helper — shows 2dp, hover reveals full precision + copy ───

function CopyableValue({ raw, children }: { raw: string; children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(raw);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <span className="group/val inline-flex items-center gap-1 relative" title={raw}>
      {children}
      <button
        onClick={copy}
        className="opacity-0 group-hover/val:opacity-100 transition-opacity duration-150 text-muted-foreground hover:text-foreground p-0.5 -mr-1"
        aria-label="Copy full value"
      >
        {copied
          ? <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
          : <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
        }
      </button>
    </span>
  );
}

function GHS({ value }: { value: number }) {
  return <CopyableValue raw={String(value)}>{formatGHS(value)}</CopyableValue>;
}

function Num({ value, dp = 2, suffix = '' }: { value: number; dp?: number; suffix?: string }) {
  return <CopyableValue raw={String(value)}>{value.toFixed(dp)}{suffix}</CopyableValue>;
}

// ----------- component -----------
export default function RenewalPricingPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [inputs, setInputs] = useState<RenewalInputs>(getDefaultRenewalInputs());
  const hasLoadedRef = useRef(false);
  const [savedInputs, setSavedInputs] = useState<RenewalInputs>(getDefaultRenewalInputs());

  // ---- DB fetch ----
  const { isLoading, data: dbRows } = useQuery({
    queryKey: ['vehicle_pricings_renewal'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicle_pricings')
        .select('key, value')
        .like('key', 'renewal_%');
      if (error) throw error;
      return data ?? [];
    },
    refetchOnWindowFocus: false,
  });

  // Load DB values once on mount
  useEffect(() => {
    if (dbRows && dbRows.length > 0 && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      const loaded = dbRowsToRenewalInputs(dbRows);
      setInputs(loaded);
      setSavedInputs(loaded);
    }
  }, [dbRows]);

  const hasChanges = JSON.stringify(inputs) !== JSON.stringify(savedInputs);

  // ---- DB save ----
  const saveMutation = useMutation({
    mutationFn: async (current: RenewalInputs) => {
      const rows = renewalInputsToDbRows(current);
      const { error } = await supabase
        .from('vehicle_pricings')
        .upsert(rows, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle_pricings_renewal'] });
      setSavedInputs(inputs);
      toast({ title: 'Saved', description: 'Renewal inputs persisted.' });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  // ---- input helpers ----
  function updateInput<K extends keyof RenewalInputs>(key: K, value: RenewalInputs[K]) {
    setInputs((prev) => ({ ...prev, [key]: value }));
  }

  function updateNumericInput(key: keyof RenewalInputs, raw: string) {
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) updateInput(key, parsed);
    else if (raw === '' || raw === '-') updateInput(key, 0);
  }

  // ---- compute ----
  const outputs = useMemo(() => computeRenewal(inputs), [inputs]);

  // ---- loading state ----
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-[260px]" />
          <Skeleton className="mt-2 h-4 w-[360px]" />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-[500px]" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2">Insurance Renewals</h1>
          <p className="text-muted-foreground">
            Simulate renewal financing to compute the additional weekly charge for the driver.
          </p>
        </div>
        <Button
          onClick={() => saveMutation.mutate(inputs)}
          disabled={!hasChanges || saveMutation.isPending}
        >
          {saveMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Inputs
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 items-start">
        {/* ==================== INPUTS ==================== */}
        <div className="space-y-4 lg:sticky lg:top-20">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Renewal Inputs</CardTitle>
              <CardDescription>Adjust values to simulate renewal financing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Cover period */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Cover Period</Label>
                <Select
                  value={String(inputs.coverPeriodMonths)}
                  onValueChange={(v) => updateInput('coverPeriodMonths', parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 months</SelectItem>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="9">9 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Insurance premium & driver contribution */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Insurance Premium (GHS)</Label>
                  <Input
                    type="number"
                    value={inputs.insurancePremium || ''}
                    onChange={(e) => updateNumericInput('insurancePremium', e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Driver Contribution (GHS)</Label>
                  <Input
                    type="number"
                    value={inputs.driverContribution || ''}
                    onChange={(e) => updateNumericInput('driverContribution', e.target.value)}
                  />
                </div>
              </div>

              {/* Primelane funded (derived) */}
              <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
                <span className="text-xs text-muted-foreground">Primelane Funded</span>
                <span className="text-sm font-semibold">{formatGHS(outputs.primelaneFunded)}</span>
              </div>

              <Separator />

              {/* Annual interest rate slider */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Annual Interest Rate</Label>
                  <span className="text-sm font-semibold tabular-nums">
                    {(inputs.annualInterestRate * 100).toFixed(1)}%
                  </span>
                </div>
                <Slider
                  value={[inputs.annualInterestRate * 100]}
                  onValueChange={([val]) => updateInput('annualInterestRate', val / 100)}
                  min={10}
                  max={100}
                  step={0.1}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ==================== OUTPUTS ==================== */}
        <div className="space-y-4">
          {/* Renewal Weekly Charge */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Renewal Weekly Charge
              </CardTitle>
              <CardDescription>
                Additional amount added to the driver's weekly rate
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold tracking-tight">
                <CopyableValue raw={String(outputs.renewalWeekly)}>
                  {formatGHS(outputs.renewalWeekly)}
                  <span className="text-base font-normal text-muted-foreground ml-1">/ week</span>
                </CopyableValue>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Over {inputs.coverPeriodMonths} months ({outputs.repaymentWeeks.toFixed(1)} weeks)
              </p>
            </CardContent>
          </Card>

          {/* Financing Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Financing Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Insurance Premium</span>
                <span className="font-medium"><GHS value={inputs.insurancePremium} /></span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Driver Contribution</span>
                <span className="font-medium">−<GHS value={inputs.driverContribution} /></span>
              </div>
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Primelane Funded</span>
                <span className="font-semibold"><GHS value={outputs.primelaneFunded} /></span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Annual Interest Rate</span>
                <span className="font-medium"><Num value={outputs.effectiveAnnualRate} suffix="%" /></span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Interest</span>
                <span className="font-medium"><GHS value={outputs.totalInterest} /></span>
              </div>
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total to Repay</span>
                <span className="font-semibold"><GHS value={outputs.totalRepaid} /></span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Repayment Period</span>
                <span className="font-medium">
                  <CopyableValue raw={String(outputs.repaymentWeeks)}>{inputs.coverPeriodMonths} months ({outputs.repaymentWeeks.toFixed(1)} weeks)</CopyableValue>
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
